// GET /public/config → the operator's branding and checkout behaviour (docs/CONTRACT-ADMIN.md).
// Everything that arrives is untrusted text: each field is validated on its own and falls back to
// the documented first-boot default, so a partial, older or malformed response still renders.
import type { AssetSymbol, ChainId, Locale, PublicConfig, ThemePreference } from "@core/types";
import { normalizeHex } from "./color";

export type { PublicConfig };

export interface CheckoutConfig extends PublicConfig {
  /** "server" = parsed from /public/config; "default" = the endpoint was missing or unreachable. */
  source: "server" | "default";
}

export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "sk"];

/** Name of the software, used only for the optional "Powered by" line. */
export const PRODUCT_NAME = "Stablecoin Checkout";

/** First-boot defaults from docs/CONTRACT-ADMIN.md, "Settings defaults". */
export const DEFAULT_CONFIG: CheckoutConfig = {
  brand: {
    name: PRODUCT_NAME,
    logoUrl: null,
    primaryColor: "#4f46e5",
    accentColor: "#10b981",
    supportEmail: null,
    supportUrl: null,
    termsUrl: null,
    privacyUrl: null,
    footerText: null,
    showPoweredBy: true,
  },
  checkout: {
    defaultExpiryMinutes: 15,
    walletModeEnabled: true,
    exchangeModeEnabled: true,
    defaultTheme: "system",
    defaultLocale: "en",
    enabledLocales: ["en", "sk"],
    redirectDelaySec: 5,
    showExchangeInstructions: true,
  },
  chains: [],
  demoMode: false,
  source: "default",
};

const CHAIN_IDS: readonly ChainId[] = ["solana-devnet", "solana-mainnet", "base-sepolia", "base-mainnet"];
const ASSETS: readonly AssetSymbol[] = ["USDC", "USDT"];
const THEMES: readonly ThemePreference[] = ["system", "light", "dark"];

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {});
const bool = (v: unknown, d: boolean) => (typeof v === "boolean" ? v : d);

function text(v: unknown, max: number): string | null {
  if (typeof v !== "string") return null;
  // Collapse whitespace and strip control characters; the value is rendered as text, never HTML.
  // eslint-disable-next-line no-control-regex
  const s = v.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return null;
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

/**
 * Absolute http(s) URL, or https only (plus localhost) with `httpsOnly`. Anything else —
 * javascript:, data:, relative paths — is dropped.
 */
export function safeUrl(v: unknown, opts: { httpsOnly?: boolean } = {}): string | null {
  if (typeof v !== "string" || v.length > 2048) return null;
  let u: URL;
  try {
    u = new URL(v.trim());
  } catch {
    return null;
  }
  const local = u.hostname === "localhost" || u.hostname === "127.0.0.1";
  if (u.protocol === "https:") return u.toString();
  if (u.protocol === "http:" && (local || !opts.httpsOnly)) return u.toString();
  return null;
}

const EMAIL = /^[^\s@<>"'`]+@[^\s@<>"'`]+\.[^\s@<>"'`]+$/;
export function safeEmail(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s.length <= 254 && EMAIL.test(s) ? s : null;
}

function int(v: unknown, min: number, max: number, d: number): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : d;
}

function locales(v: unknown): Locale[] {
  if (!Array.isArray(v)) return [...DEFAULT_CONFIG.checkout.enabledLocales];
  const out = [...new Set(v.filter((l): l is Locale => SUPPORTED_LOCALES.includes(l as Locale)))];
  return out.length ? out : [...DEFAULT_CONFIG.checkout.enabledLocales];
}

/** Accepts a PublicConfig (or `{ config: PublicConfig }`) and returns a complete, sanitised config. */
export function parsePublicConfig(input: unknown): CheckoutConfig {
  const root = obj(input);
  const data = "brand" in root || "checkout" in root ? root : obj(root.config);
  const b = obj(data.brand);
  const c = obj(data.checkout);
  const d = DEFAULT_CONFIG;

  const enabledLocales = locales(c.enabledLocales);
  const defaultLocale = enabledLocales.includes(c.defaultLocale as Locale) ? (c.defaultLocale as Locale) : enabledLocales[0]!;

  const chains = Array.isArray(data.chains)
    ? data.chains
        .map(obj)
        .filter((x) => CHAIN_IDS.includes(x.chain as ChainId))
        .map((x) => ({
          chain: x.chain as ChainId,
          assets: Array.isArray(x.assets) ? x.assets.filter((a): a is AssetSymbol => ASSETS.includes(a as AssetSymbol)) : [],
          testnet: bool(x.testnet, String(x.chain).endsWith("devnet") || String(x.chain).endsWith("sepolia")),
        }))
    : [];

  return {
    brand: {
      name: text(b.name, 60) ?? d.brand.name,
      logoUrl: safeUrl(b.logoUrl, { httpsOnly: true }),
      primaryColor: normalizeHex(b.primaryColor) ?? d.brand.primaryColor,
      accentColor: normalizeHex(b.accentColor) ?? d.brand.accentColor,
      supportEmail: safeEmail(b.supportEmail),
      supportUrl: safeUrl(b.supportUrl),
      termsUrl: safeUrl(b.termsUrl),
      privacyUrl: safeUrl(b.privacyUrl),
      footerText: text(b.footerText, 160),
      showPoweredBy: bool(b.showPoweredBy, d.brand.showPoweredBy),
    },
    checkout: {
      defaultExpiryMinutes: int(c.defaultExpiryMinutes, 5, 1440, d.checkout.defaultExpiryMinutes),
      walletModeEnabled: bool(c.walletModeEnabled, d.checkout.walletModeEnabled),
      exchangeModeEnabled: bool(c.exchangeModeEnabled, d.checkout.exchangeModeEnabled),
      defaultTheme: THEMES.includes(c.defaultTheme as ThemePreference) ? (c.defaultTheme as ThemePreference) : d.checkout.defaultTheme,
      defaultLocale,
      enabledLocales,
      redirectDelaySec: int(c.redirectDelaySec, 0, 30, d.checkout.redirectDelaySec),
      showExchangeInstructions: bool(c.showExchangeInstructions, d.checkout.showExchangeInstructions),
    },
    chains,
    demoMode: bool(data.demoMode, false),
    source: "server",
  };
}

/**
 * Pay modes to offer: what the server allows for this invoice, narrowed by the operator's switches.
 * Newer servers already apply the switches; this keeps an older server's response in line too.
 */
export function allowedModes<M extends "wallet" | "exchange">(invoiceModes: readonly M[], cfg: CheckoutConfig): M[] {
  return invoiceModes.filter((m) => (m === "wallet" ? cfg.checkout.walletModeEnabled : cfg.checkout.exchangeModeEnabled));
}
